import { Injectable, OnDestroy } from '@angular/core';
import { IReportNotification } from '@app/layout/sidebar-notification-panel/IReportNotification';
import { HubConnection, HubConnectionState } from '@node_modules/@aspnet/signalr/dist/esm';
import { SignalRAspNetCoreHelper } from '@shared/helpers/SignalRAspNetCoreHelper';
import { StockAndProductPriceInformationDto } from '@shared/service-proxies/service-proxies';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable()
export class NotificationService implements OnDestroy {
    // Usar ReplaySubject con buffer de 1 para no perder el último evento
    private updateProductCacheSubject = new ReplaySubject<boolean>(1);
    private updateProductPricesSubject = new ReplaySubject<string>(1);
    private downloadnProductsReportSubject = new ReplaySubject<any>(1);
    private downloadProductPricesSubject = new ReplaySubject<any>(1);
    private downloadMinimumStockReportSubject = new ReplaySubject<any>(1);
    private downloadnSellerMonthlyReportSubject = new ReplaySubject<any>(1);
    private downloadSalesRankingBySellerReportSubject = new ReplaySubject<any>(1);
    private downloadPromotionsReportSubject = new ReplaySubject<any>(1);
    private downloadProductInfoReportSubject = new ReplaySubject<any>(1);
    private stockInformationSubject = new ReplaySubject<StockAndProductPriceInformationDto>(1);
    private closeUserConnectionSubject = new ReplaySubject<boolean>(1);
    private newNotificationSubject = new ReplaySubject<boolean>(1);
    private purchaseOrderSentSubject = new ReplaySubject<string>(1);

    // Subject para notificar cuando la conexión está lista
    private connectionReadySubject = new BehaviorSubject<boolean>(false);
    connectionReady$ = this.connectionReadySubject.asObservable();

    //reportes
    private reportNotifications: IReportNotification[] = [];
    private reportNotificationSubject = new ReplaySubject<IReportNotification[]>(1);
    reportNotification$ = this.reportNotificationSubject.asObservable();

    // Para evitar registrar handlers duplicados
    private registeredDynamicKeys = new Set<string>();

    updateProductCache$ = this.updateProductCacheSubject.asObservable();
    updateProductPrices$ = this.updateProductPricesSubject.asObservable();
    downloadProductPricesReport$ = this.downloadPromotionsReportSubject.asObservable();
    donwloadProductReport$ = this.downloadnProductsReportSubject.asObservable();
    downloadMinimumStockReport$ = this.downloadMinimumStockReportSubject.asObservable();
    downloadnSellerMonthly$ = this.downloadnSellerMonthlyReportSubject.asObservable();
    downloadSalesRankingBySeller$ = this.downloadSalesRankingBySellerReportSubject.asObservable();
    downloadPromotions$ = this.downloadPromotionsReportSubject.asObservable();
    stockListInformation$ = this.stockInformationSubject.asObservable();
    downloadProductInfoReport$ = this.downloadProductInfoReportSubject.asObservable();
    closeUserConnection$ = this.closeUserConnectionSubject.asObservable();
    newNotification$ = this.newNotificationSubject.asObservable();
    purchaseOrderSent$ = this.purchaseOrderSentSubject.asObservable();


    private isManuallyDisconnected: boolean = false;
    private isConnecting: boolean = false; // Bandera para evitar conexiones duplicadas
    private hubConnection: HubConnection;

    // Configuración de reconexión con backoff exponencial
    private reconnectAttempts: number = 0;
    private maxReconnectAttempts: number = 15;
    private baseReconnectInterval: number = 1000; // 1 segundo base

    // Heartbeat para detectar desconexiones silenciosas
    private connectionCheckInterval: any;
    private connectionCheckIntervalMs: number = 30000; // 30 segundos

    constructor() {
        this.setupNotificationHub();
    }

    ngOnDestroy(): void {
        this.stopConnectionCheck(); // Asegurar limpieza del heartbeat
        this.disconnect();
    }

    /** Verifica si la conexión está activa */
    private isConnected(): boolean {
        return this.hubConnection && this.hubConnection.state === HubConnectionState.Connected;
    }

    /** Retorna una promesa que se resuelve cuando la conexión está lista 
     * @param timeoutMs Tiempo máximo de espera en milisegundos (por defecto 30 segundos)
     */
    waitForConnection(timeoutMs: number = 30000): Promise<void> {
        // Si ya está conectado, resolver inmediatamente
        if (this.connectionReadySubject.value) {
            return Promise.resolve();
        }
        
        // Si no está conectando y no está conectado, iniciar conexión
        if (!this.isConnecting && !this.isConnected()) {
            this.setupNotificationHub();
        }
        
        return new Promise((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                subscription.unsubscribe();
                reject(new Error('Timeout esperando conexión SignalR en NotificationService'));
            }, timeoutMs);
            
            const subscription = this.connectionReady$.pipe(
                filter(ready => ready),
                take(1)
            ).subscribe(() => {
                clearTimeout(timeoutId);
                subscription.unsubscribe();
                resolve();
            });
        });
    }

    /** Fuerza la reconexión solo si es necesario */
    forceReconnect(): void {
        this.isManuallyDisconnected = false;
        this.reconnectAttempts = 0; // Resetear contador al forzar reconexión
        
        // Si ya está conectado o conectando, no hacer nada
        if (this.isConnected()) {
            console.log('NotificationService: Ya conectado, no es necesario reconectar.');
            return;
        }
        
        if (this.isConnecting) {
            console.log('NotificationService: Ya hay una conexión en proceso.');
            return;
        }
        
        this.setupNotificationHub();
    }

    disconnect(): void {
        this.stopConnectionCheck(); // Detener heartbeat
        this.isManuallyDisconnected = true;
        this.isConnecting = false;
        this.reconnectAttempts = 0; // Resetear contador
        if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
            this.hubConnection.stop()
                .then(() => {
                    console.log('Desconectado del hub de NotificationService');
                })
                .catch(err => console.error('Error al desconectar NotificationService: ', err));
        }
    }

    private setupNotificationHub(): void {
        // Evitar conexiones duplicadas
        if (this.isConnecting) {
            console.log('NotificationService: Conexión ya en proceso, ignorando llamada duplicada.');
            return;
        }
        
        if (this.isConnected()) {
            console.log('NotificationService: Ya conectado, no es necesario reconectar.');
            this.connectionReadySubject.next(true);
            return;
        }
        
        this.isConnecting = true;
        this.isManuallyDisconnected = false;

        SignalRAspNetCoreHelper.initSignalR(() => {
            console.log('SignalR inicializado desde NotificationService.');
            abp.signalr.startConnection(abp.appPath + 'signalr-notificationHub', (connection) => {
                console.log('Configurando conexión SignalR en NotificationService.');
                this.hubConnection = connection;

                this.registerOnServerEvents(connection);
                // Re-registrar handlers dinámicos después de reconexión
                this.reRegisterDynamicHandlers(connection);

                connection.onclose(() => {
                    console.warn('Conexión SignalR cerrada en NotificationService.');
                    this.isConnecting = false;
                    this.connectionReadySubject.next(false);
                    if (!this.isManuallyDisconnected) {
                        this.reconnect();
                    }
                });
            })
            .then(() => {
                console.log('Conexión SignalR establecida en NotificationService.');
                this.isConnecting = false;
                this.reconnectAttempts = 0; // Resetear contador de reconexión
                this.connectionReadySubject.next(true);
                this.startConnectionCheck(); // Iniciar heartbeat
                abp.event.trigger('notificationHub.connected');
            })
            .catch((err) => {
                console.error('Error al conectar SignalR desde NotificationService:', err);
                this.isConnecting = false;
                this.connectionReadySubject.next(false);
                this.reconnect();
            });
        });
    }

    private reconnect(): void {
        // No reconectar si está desconectado manualmente o ya conectando
        if (this.isManuallyDisconnected || this.isConnecting) {
            return;
        }
        
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('NotificationService: Máximo de intentos de reconexión alcanzado (' + this.maxReconnectAttempts + ')');
            return;
        }
        
        // Backoff exponencial con jitter: evita que todos los clientes reconecten al mismo tiempo
        const exponentialDelay = Math.min(
            this.baseReconnectInterval * Math.pow(2, this.reconnectAttempts),
            30000 // máximo 30 segundos
        );
        const jitter = Math.random() * 1000; // añadir 0-1 segundo de jitter
        const delay = exponentialDelay + jitter;
        
        this.reconnectAttempts++;
        console.log(`NotificationService: Reconexión intento ${this.reconnectAttempts}/${this.maxReconnectAttempts} en ${Math.round(delay)}ms...`);
        
        setTimeout(() => this.setupNotificationHub(), delay);
    }

    /** Inicia verificación periódica de conexión (heartbeat) */
    private startConnectionCheck(): void {
        this.stopConnectionCheck(); // Limpiar cualquier intervalo previo
        this.connectionCheckInterval = setInterval(() => {
            if (!this.isConnected() && !this.isManuallyDisconnected && !this.isConnecting) {
                console.warn('NotificationService: Conexión perdida detectada por heartbeat, reconectando...');
                this.reconnect();
            }
        }, this.connectionCheckIntervalMs);
    }

    /** Detiene la verificación periódica de conexión */
    private stopConnectionCheck(): void {
        if (this.connectionCheckInterval) {
            clearInterval(this.connectionCheckInterval);
            this.connectionCheckInterval = null;
        }
    }

    private reRegisterDynamicHandlers(connection: any): void {
        // Re-registrar todos los handlers dinámicos después de una reconexión
        this.registeredDynamicKeys.forEach(key => {
            connection.on(key, (data: StockAndProductPriceInformationDto) => {
                this.stockInformationSubject.next(data);
            });
        });
    }

    private registerOnServerEvents(connection: any): void {
        connection.on('updateProductCache', (message: boolean) => {
            this.updateProductCacheSubject.next(message);
        });

        connection.on('updateProductPrices', (message: string) => {
            this.updateProductPricesSubject.next(message);
        });

        connection.on('downloadProductsReport', (excelData: any) => {
            this.downloadnProductsReportSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });

        connection.on('downloadProductPricesReport', (excelData: any) => {
            this.downloadProductPricesSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });

        connection.on('downloadMinimumStockReport', (excelData: any) => {
            this.downloadMinimumStockReportSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });

        connection.on('downloadSalesRankingBySeller', (excelData: any) => {
            this.downloadSalesRankingBySellerReportSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });

        connection.on('downloadPromotionsReport', (excelData: any) => {
            this.downloadPromotionsReportSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });
        

        connection.on('downloadSellerMonthlyReport', (excelData: any) => {
            this.downloadnSellerMonthlyReportSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });

        connection.on('downloadExcelProductInformation', (excelData: any) => {
            this.downloadProductInfoReportSubject.next(excelData);
            this.addReportNotification({
                name: excelData.fileName,
                date: new Date().toLocaleDateString(),
                route: excelData.fileData
            });
            this.newNotificationSubject.next(true);
        });

        connection.on('closeUserSession', (closeConnection: boolean) => {
            this.closeUserConnectionSubject.next(closeConnection);
        })

        connection.on('PurchaseOrderSent', (deliveryNoteNumber: string) => {
            this.purchaseOrderSentSubject.next(deliveryNoteNumber);
            this.addReportNotification({
                name: abp.utils.formatString(abp.localization.localize('PurchaseOrderSentToCentral', 'PosMaer'), deliveryNoteNumber),
                date: new Date().toLocaleDateString(),
                route: ''
            });
            this.newNotificationSubject.next(true);
        })
    }

    onUpdateProductCache(): Observable<boolean> {
        return this.updateProductCache$;
    }

    onUpdateProductPrices(): Observable<string> {
        return this.updateProductPrices$;
    }

    onDownloadProductsPricesReport(): Observable<any> {
        return this.downloadProductPricesReport$;
    }

    onDownloadProductsReport(): Observable<any> {
        return this.donwloadProductReport$;
    }

    onDownloadMinimumStockReport(): Observable<any> {
        return this.downloadMinimumStockReport$;
    }

    onDownloadSellerMonthlyReport(): Observable<any> {
        return this.downloadnSellerMonthly$;
    }

    onDownloadSalesRankingBySeller(): Observable<any> {
        return this.downloadSalesRankingBySeller$;
    }

    onDownloadPromotions(): Observable<any> {
        return this.downloadPromotions$;
    }
    onDownloadProductInfoReport(): Observable<any> {
        return this.downloadProductInfoReport$;
    }

    onStockOrPriceChange(key: string): Observable<any> {
        // Evitar registrar el mismo handler múltiples veces
        if (!this.registeredDynamicKeys.has(key)) {
            this.registeredDynamicKeys.add(key);
            
            // Solo registrar si la conexión ya existe
            if (this.hubConnection) {
                this.hubConnection.on(key, (data: StockAndProductPriceInformationDto) => {
                    this.stockInformationSubject.next(data);
                });
            }
        }
        return this.stockListInformation$;
    }

    onCloseUserConnection(): Observable<boolean>{
        return this.closeUserConnection$;
    }

    onPurchaseOrderSent(): Observable<string> {
        return this.purchaseOrderSent$;
    }

    private addReportNotification(notification: IReportNotification) {
        this.reportNotifications.push(notification);
        this.reportNotificationSubject.next([...this.reportNotifications]);
    }

    // Método para eliminar una notificación específica (por ejemplo, por ruta)
    removeReportNotification(route: string) {
        this.reportNotifications = this.reportNotifications.filter(n => n.route !== route);
        this.reportNotificationSubject.next([...this.reportNotifications]);
    }
}
